/* eslint-disable @typescript-eslint/no-this-alias */

import {useApolloClient, ApolloClient, gql} from "@apollo/client";
import {HookClass} from "@dvrt/graphql-widgets";
import {DataSource} from "./dataSource";
import {CallGroupMembershipInput, NewMemberInfo} from "@/components/call-group/members/types";
import {Dispatch, SetStateAction, useState} from "react";
import deepEqual from "deep-equal";
import {createRollbackOnErrorContext} from "@/apollo-client/factory";

type ExpandRecursively<T> = T extends object
    ? T extends infer O
        ? {[K in keyof O]: ExpandRecursively<O[K]>}
        : never
    : T;

export type FormMemberInput = ExpandRecursively<
    Omit<CallGroupMembershipInput, "callOrder" | "sectionIndex" | "groupId"> & {
        shortNumber: string;
        displayName: string;
    }
>;

export type FormValues = {
    members: FormMemberInput[];
    sections: boolean[]; // true if there is a section before the member (so if the current member is the first member of a section)
};

export class Form extends HookClass {
    private groupId!: number;
    dataSource!: DataSource;

    values!: FormValues;
    setValues!: Dispatch<SetStateAction<FormValues>>;

    protected client!: ApolloClient<object>;

    useHooks(dataSource: DataSource) {
        this.groupId = dataSource.groupId;
        this.dataSource = dataSource;
        [this.values, this.setValues] = useState(this.initialData);
        this.client = useApolloClient();
    }

    private getMemberSectionIndex(index: number): number {
        if (index === 0) return 1;
        return this.values.sections.slice(0, index + 1).filter((s) => s).length;
    }

    private memberInputHasChanged(member: FormMemberInput, index: number): boolean | undefined {
        const initialOrder = this.initialData.members.findIndex((m) => m.numberId === member.numberId);
        if (initialOrder === -1) return undefined;

        const initialMember = this.initialData.members[initialOrder];
        const initialSectionIndex = this.initialData.sections.slice(0, initialOrder + 1).filter((s) => s).length;

        return (
            !deepEqual(initialMember, member) ||
            index !== initialOrder ||
            this.getMemberSectionIndex(index) !== initialSectionIndex
        );
    }

    private generateMutationForMember(member: FormMemberInput, index: number): string | undefined {
        const changed = this.memberInputHasChanged(member, index);

        if (changed === false) return undefined;

        const input = {
            ...structuredClone(member),
            groupId: this.groupId,
            callOrder: index,
            sectionIndex: this.getMemberSectionIndex(index),
        };

        delete (input as {shortNumber?: string}).shortNumber;
        delete (input as {displayName?: string}).displayName;

        const vars: string[] = [];
        for (const [key, value] of Object.entries(input)) {
            vars.push(`${key}: ${value}`);
        }

        return `saveItem${member.numberId}: saveGroupMembership(${vars.join(" ")}) {errors {location type context}}`;
    }

    private generateMutationForDeletedMember(member: FormMemberInput): string {
        return `deleteItem${member.numberId}: deleteGroupMembership(numberId: ${member.numberId} groupId: ${this.groupId}) {type}`;
    }

    updateValues(updater: (values: FormValues) => FormValues): void {
        // structured clone is necessary to trigger a rerender everytime a property changes of an object in an array
        // otherwise useMemo does not know when to recalculate
        this.setValues((values) => updater(structuredClone(values)));
    }

    get initialData(): FormValues {
        return {
            members: this.dataSource.data.current.map((m) => ({
                numberId: m.numberId,
                shortNumber: m.number.shortNumber,
                displayName: m.number.extension.displayName,
                controllingLine: m.controllingLine,
                allowEditAddressBook: m.allowEditAddressBook,
                overrideNumber: m.number.extension.alternateCallerNumberId === this.dataSource.groupInfo.numberId,
            })),
            sections: this.dataSource.data.current.map((m, index) => {
                if (index === 0) return true;
                const previousMember = this.dataSource.data.current[index - 1];
                if (m.sectionIndex > previousMember.sectionIndex) return true;
                else return false;
            }),
        };
    }

    addNewMembers(members: NewMemberInfo[], afterIndex?: number): void {
        afterIndex ??= this.values.members.length - 1;

        // const memberJustBefore = this.values.members[afterIndex];

        const newMembers: FormMemberInput[] = members.map((member) => ({
            shortNumber: member.shortNumber,
            displayName: member.displayName,
            numberId: member.numberId,
            controllingLine: false,
            allowEditAddressBook: false,
            overrideNumber: false,
            // FIXME somebody thought this is important, but sectionIndex is not a field of members
            // sectionIndex: memberJustBefore?.sectionIndex ?? 1,
            sectionIndex: 1,
        }));

        this.updateValues((values) => {
            values.members.splice(afterIndex + 1, 0, ...newMembers);
            values.sections.splice(afterIndex + 1, 0, ...newMembers.map((_) => false));
            return values;
        });
    }

    removeMember(numberId: number): void {
        this.updateValues((values) => {
            const index = values.members.findIndex((m) => m.numberId === numberId);
            values.members.splice(index, 1);
            values.sections.splice(index, 1);
            return values;
        });
    }

    updateMember(numberId: number, newValues: Partial<FormMemberInput>): void {
        const member = this.values.members.find((member) => member.numberId === numberId);
        if (!member) return;

        this.updateValues((values) => {
            const index = values.members.findIndex((m) => m.numberId === numberId);
            values.members.splice(index, 1, {...member, ...newValues});
            return values;
        });
    }

    getShiftedSections(originalBeforeMemberIndex: number, direction: "up" | "down"): boolean[] {
        const otherSectionOffset = direction === "down" ? 1 : -1;
        const otherSectionIndex = originalBeforeMemberIndex + otherSectionOffset;

        return structuredClone(this.values.sections).map((s, i) => {
            if (i === originalBeforeMemberIndex) return false;
            if (i === otherSectionIndex) return true;
            return s;
        });
    }

    private generateMutations(): string[] {
        const self = this;

        function* generator() {
            for (const [index, member] of self.values.members.entries()) {
                const mutation = self.generateMutationForMember(member, index);
                if (mutation !== undefined) yield mutation;
            }
            for (const member of self.initialData.members) {
                if (!self.values.members.find((m) => m.numberId === member.numberId))
                    yield self.generateMutationForDeletedMember(member);
            }
        }

        return Array.from(generator());
    }

    async submit(): Promise<boolean> {
        const mutations = this.generateMutations();
        const mutation = gql(`mutation SaveGroupMemberships { ${mutations.join("\n")} }`);

        const res = await this.client.mutate({
            mutation,
            context: createRollbackOnErrorContext(),
        });

        const success = !!res.data;

        if (success) {
            await this.dataSource.fetch();
            this.updateValues((_) => this.initialData);
        }

        return success;
    }

    reset() {
        this.setValues(this.initialData);
    }

    get hasChanged(): boolean {
        return !deepEqual(this.initialData, this.values);
    }
}
